import { createSelector, createSlice } from "@reduxjs/toolkit";
import type { PayloadAction } from "@reduxjs/toolkit";
import { setAsset } from "./assetSlice";
import type { RootState } from "~/store";
import type { BridgeModel } from "~/types/bridge";
import type { CloudImage } from "~/types/CloudImage";
import type { Annotation } from "~/types/annotations";

export type SurveyDataType = "models" | "images" | "annotations" | "messages" | "survey";

export type SurveyHistoryEntry = {
  models?: BridgeModel[];
  images?: CloudImage[];
  annotations?: Annotation[];
  messages?: any[];
  survey?: any;
  updatedAt?: string;
};

interface HistoryState {
  bySurveyId: Record<string, SurveyHistoryEntry>;
}

const initialState: HistoryState = {
  bySurveyId: {},
};

const historySlice = createSlice({
  name: "history",
  initialState,
  reducers: {
    setSurveyDataByType: (
      state,
      action: PayloadAction<{
        surveyId: string | number;
        dataType: SurveyDataType;
        data: any;
      }>
    ) => {
      const { surveyId, dataType, data } = action.payload;
      const surveyKey = String(surveyId);
      const existingEntry = state.bySurveyId[surveyKey] || {};

      state.bySurveyId[surveyKey] = {
        ...existingEntry,
        [dataType]: data,
        updatedAt: new Date().toISOString(),
      };
    },
    setSurveyData: (
      state,
      action: PayloadAction<{
        surveyId: string | number;
        data: Partial<SurveyHistoryEntry>;
      }>
    ) => {
      const { surveyId, data } = action.payload;
      const surveyKey = String(surveyId);
      const existingEntry = state.bySurveyId[surveyKey] || {};

      state.bySurveyId[surveyKey] = {
        ...existingEntry,
        ...data,
        updatedAt: new Date().toISOString(),
      };
    },
    clearSurveyDataType: (
      state,
      action: PayloadAction<{
        surveyId: string | number;
        dataType: SurveyDataType;
      }>
    ) => {
      const { surveyId, dataType } = action.payload;
      const surveyKey = String(surveyId);
      const surveyEntry = state.bySurveyId[surveyKey];
      if (!surveyEntry) return;

      delete surveyEntry[dataType];
      surveyEntry.updatedAt = new Date().toISOString();

      const hasAnyDataType =
        surveyEntry.models !== undefined ||
        surveyEntry.images !== undefined ||
        surveyEntry.annotations !== undefined ||
        surveyEntry.messages !== undefined ||
        surveyEntry.survey !== undefined;

      if (!hasAnyDataType) {
        delete state.bySurveyId[surveyKey];
      }
    },
    clearSurveyHistoryById: (state, action: PayloadAction<string | number>) => {
      delete state.bySurveyId[String(action.payload)];
    },
    clearSurveyHistory: (state) => {
      state.bySurveyId = {};
    },
  },
  extraReducers: (builder) => {
    builder.addCase(setAsset, (state) => {
      state.bySurveyId = {};
    });
  },
});

export default historySlice.reducer;

export const {
  setSurveyDataByType,
  setSurveyData,
  clearSurveyDataType,
  clearSurveyHistoryById,
  clearSurveyHistory,
} = historySlice.actions;

export const selectSurveyHistoryMap = (state: RootState) => state.history.bySurveyId;

export const selectSurveyHistoryById = (
  state: RootState,
  surveyId: string | number | null | undefined
): SurveyHistoryEntry | null => {
  if (surveyId === null || surveyId === undefined) return null;
  return state.history.bySurveyId[String(surveyId)] || null;
};

export const selectSurveyDataTypeBySurveyId = (
  state: RootState,
  surveyId: string | number | null | undefined,
  dataType: SurveyDataType
) => {
  const surveyEntry = selectSurveyHistoryById(state, surveyId);
  return surveyEntry ? surveyEntry[dataType] : undefined;
};

export const selectDataTypeFromAllSurveys = (state: RootState, dataType: SurveyDataType) => {
  const result: Record<string, any> = {};
    for (const [surveyId, surveyEntry] of Object.entries(state.history.bySurveyId)) {
        if (surveyEntry[dataType] !== undefined) {
            result[surveyId] = surveyEntry[dataType];
        }
    }
  return result;
};

export const makeSelectDataTypeFromAllSurveys = (dataType: SurveyDataType) =>
  createSelector(selectSurveyHistoryMap, (historyMap) => {
    const result: Record<string, any> = {};
    for (const [surveyId, surveyEntry] of Object.entries(historyMap)) {
      if (surveyEntry[dataType] !== undefined) {
        result[surveyId] = surveyEntry[dataType];
      }
    }
    return result;
  });
